import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { api } from "../../lib/api";
import type { BusinessStatus } from "../../types/auth";
import { Button } from "../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { Input } from "../../app/components/ui/input";
import { Badge } from "../../app/components/ui/badge";
import { Progress } from "../../app/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/ui/select";
import { useAuth } from "../../providers/AuthProvider";
import { TableLayout, type TableColumn } from "../../components/dashboard";

type Row = {
  id: string;
  name: string;
  status: BusinessStatus;
  ownerUserId: string;
  ownerEmail: string;
  ownerName: string;
};

type ProfileRow = {
  business_id: string;
  completion_percentage: number;
  is_complete: boolean;
  store_type: string | null;
};

function badgeVariant(s: BusinessStatus) {
  if (s === "verified") return "default";
  if (s === "rejected") return "destructive";
  return "secondary";
}

export function BusinessesManagementPage() {
  const { token } = useAuth();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [rows, setRows] = useState<Row[]>([]);
  const [profilesByBusinessId, setProfilesByBusinessId] = useState<Record<string, ProfileRow>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [bizRes, profRes] = await Promise.all([
          api.admin.listBusinesses({
            token,
            q: q.trim() || undefined,
            status: statusFilter === "all" ? undefined : (statusFilter as BusinessStatus),
          }),
          api.admin.listBusinessProfiles({
            token,
            q: q.trim() || undefined,
            page: 1,
            perPage: 100,
          }),
        ]);

        const byId: Record<string, ProfileRow> = {};
        for (const p of profRes.data) {
          byId[p.business_id] = {
            business_id: p.business_id,
            completion_percentage: p.completion_percentage,
            is_complete: p.is_complete,
            store_type: p.store_type,
          };
        }

        if (!cancelled) {
          setRows(bizRes as Row[]);
          setProfilesByBusinessId(byId);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, q, statusFilter]);

  const profileCell = useCallback((businessId: string) => {
    const p = profilesByBusinessId[businessId];
    const pct = p?.completion_percentage ?? 0;
    return (
      <div className="min-w-0 w-full max-w-xs space-y-1">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{p?.is_complete ? "Complete" : "In progress"}</span>
          <span className="font-medium text-foreground">{pct}%</span>
        </div>
        <Progress value={pct} className="h-2" />
      </div>
    );
  }, [profilesByBusinessId]);

  const columns: TableColumn<Row>[] = useMemo(
    () => [
      { key: "name", header: "Business", render: (r) => <span className="font-medium">{r.name}</span> },
      {
        key: "owner",
        header: "Owner",
        render: (r) => (
          <div className="min-w-0 leading-tight">
            <div>{r.ownerName}</div>
            <div className="text-xs text-muted-foreground">{r.ownerEmail}</div>
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (r) => (
          <Badge variant={badgeVariant(r.status) as "default" | "secondary" | "destructive"}>{r.status}</Badge>
        ),
      },
      {
        key: "profile",
        header: "Profile completion",
        render: (r) => profileCell(r.id),
      },
      {
        key: "action",
        header: "Action",
        render: (r) => (
          <Button asChild size="sm" variant="outline" className="min-h-9">
            <Link to={`/admin/businesses/${encodeURIComponent(r.id)}/profile`}>View details</Link>
          </Button>
        ),
      },
    ],
    [profileCell],
  );

  const mobileCardRender = useCallback(
    (r: Row) => (
      <div className="space-y-3">
        <div className="font-medium">{r.name}</div>
        <div className="text-sm text-muted-foreground">
          {r.ownerName} · {r.ownerEmail}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={badgeVariant(r.status) as "default" | "secondary" | "destructive"}>{r.status}</Badge>
        </div>
        {profileCell(r.id)}
        <Button asChild size="sm" variant="outline" className="w-full min-h-10">
          <Link to={`/admin/businesses/${encodeURIComponent(r.id)}/profile`}>View details</Link>
        </Button>
      </div>
    ),
    [profileCell],
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Businesses</h1>
        <p className="text-muted-foreground">Search and filter businesses (mock).</p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>All businesses</CardTitle>
          <CardDescription>Client-side filters against mock dataset</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <Input
              placeholder="Search name or owner..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="min-h-9 w-full min-w-0 sm:max-w-md"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="min-h-9 w-full min-w-0 sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              className="min-h-9 w-full sm:w-auto"
              onClick={() => {
                setQ("");
                setStatusFilter("all");
              }}
            >
              Reset
            </Button>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No businesses match.</div>
          ) : (
            <div className="min-w-0 rounded-md border md:border-0">
              <TableLayout columns={columns} rows={rows} mobileCardRender={mobileCardRender} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
